import { useNavigate } from 'react-router-dom';
import type { DashboardDrillDown, DashboardResultRow } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { formatCell } from './format.js';

interface DrillDownModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: DashboardDrillDown | undefined;
  isLoading: boolean;
  error: string | null;
}

/** The records behind a metric value or a chart segment. Rows open the real record. */
export function DrillDownModal({
  isOpen,
  onClose,
  title,
  data,
  isLoading,
  error,
}: DrillDownModalProps): JSX.Element {
  const navigate = useNavigate();

  const open = (row: DashboardResultRow, event: React.MouseEvent): void => {
    if (!row.link) return;
    if (event.ctrlKey || event.metaKey) {
      window.open(row.link, '_blank', 'noopener,noreferrer');
      return;
    }
    onClose();
    navigate(row.link);
  };

  const heading = data?.groupLabel ? `${title} — ${data.groupLabel}` : title;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={heading}
      size="wide"
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">
            {data && data.total > data.rows.length
              ? `Showing the first ${data.rows.length} of ${data.total} records.`
              : ''}
          </span>
          <Button variant="secondary" onClick={onClose} type="button">
            Close
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : error ? (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      ) : !data || data.rows.length === 0 ? (
        <p className="py-6 text-sm text-slate-500">No records.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {data.columns.map((column) => (
                <th key={column.key} className="px-3 py-2 font-medium">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((row) => (
              <tr
                key={row.id}
                className={row.link ? 'cursor-pointer hover:bg-slate-50' : undefined}
                onClick={(event) => open(row, event)}
              >
                {data.columns.map((column) => (
                  <td key={column.key} className="px-3 py-2 text-slate-700">
                    {formatCell(row.cells[column.key] ?? null, column.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
